/**
 * The autopilot: each scan it reads the cave, picks a goal (a diamond, the exit, or something to
 * dig at), routes to it around danger, and only makes a move that look-ahead proves survivable
 * (`mover.ts`). It remembers its goal, how long since it last got closer, goals it gave up on,
 * and its escape plan. Deterministic: its only randomness is its own seeded `Rng`.
 */

import { Rng } from '../sim';
import type { CaveState, Input } from '../sim';
import { DangerMap } from './danger';
import { Explorer } from './explore';
import { Hunter } from './hunt';
import type { Fuse } from './hunt';
import { STAY } from './inputs';
import { Mover } from './mover';
import { INF, Paths } from './paths';
import { LONG_HORIZON } from './safety';
import { pickTarget } from './targets';

/** An autopilot for one running cave at a time. */
export interface Autopilot {
  /** The input for the coming scan; call once per scan, before `step`. */
  next(s: CaveState): Input;
}

/** Scans without getting closer before a goal is given up for a while. */
const STALL_LIMIT = 60;
const BAN_SCANS = 240;
/** An exploration goal once reached is not chosen again for this long. */
const VISITED_SCANS = 120;
/** Scans at a fuse waiting for a sure hit before dropping the boulder anyway (slime is slow). */
const FUSE_PATIENCE = 30;

/** Everything the pilot keeps for one cave. */
class Mind {
  private readonly danger: DangerMap;
  private readonly near: Paths;
  private readonly explorer: Explorer;
  private readonly hunter: Hunter;
  private readonly mover: Mover;
  /** Scan until which each cell is not chosen as a goal. */
  private readonly banned: Int32Array;
  private goal = -1;
  /** The goal is somewhere to dig or stand, not a target. */
  private exploring = false;
  private goalBest = INF;
  private stall = 0;
  private lastDiamonds: number;
  /** The drop being set up, while hunting creatures. */
  private fuse: Fuse | null = null;
  /** When Digby got to the current fuse cell, or -1. */
  private fuseSince = -1;

  constructor(
    s: CaveState,
    private readonly rng: Rng,
  ) {
    const n = s.w * s.h;
    this.danger = new DangerMap(n);
    this.near = new Paths(n);
    this.explorer = new Explorer(n);
    this.hunter = new Hunter(n);
    this.mover = new Mover(s);
    this.banned = new Int32Array(n);
    this.lastDiamonds = s.diamonds;
  }

  decide(s: CaveState): Input {
    const me = s.digby.y * s.w + s.digby.x;
    this.danger.build(s, this.mover.avoid);
    this.near.run(s, me, this.danger);
    const prospects = this.mover.measure(s);
    this.progress(s);
    // Short of the quota even with every diamond in reach: creatures are worth hunting first.
    const short = !s.gateOpen && prospects.diamonds < s.params.diamondsNeeded;
    const goal = this.chooseGoal(s, me, short);
    if (this.fuse !== null && this.fuse.at === me) return this.light(s, me, this.fuse);
    this.fuseSince = -1;
    const route = goal >= 0 ? this.near.route(goal, s.w, LONG_HORIZON) : [];
    return this.mover.choose(s, me, route, goal);
  }

  /**
   * In place to drop a boulder (on a creature, or through a magic wall): do it when look-ahead
   * says it pays; after a while, do it anyway if it is safe - slime and pushes take their time.
   */
  private light(s: CaveState, me: number, fuse: Fuse): Input {
    if (this.fuseSince < 0) this.fuseSince = s.scan;
    const verdict = this.mover.fuse(s, fuse.input, fuse.push);
    if (verdict === 'hit') return fuse.input;
    if (s.scan - this.fuseSince > FUSE_PATIENCE && this.mover.attempt(s, fuse.input)) return fuse.input;
    const wait = this.mover.choose(s, me, [], -1);
    return verdict === 'strain' && wait === STAY ? fuse.input : wait;
  }

  /** Give up on a goal that has not come any closer for a long time. */
  private progress(s: CaveState): void {
    if (s.diamonds !== this.lastDiamonds) {
      this.lastDiamonds = s.diamonds;
      this.stall = 0;
    }
    if (this.goal < 0) return;
    const d = this.near.dist[this.goal] ?? INF;
    if (d < this.goalBest) {
      this.goalBest = d;
      this.stall = 0;
    } else if (++this.stall > STALL_LIMIT) {
      this.banned[this.goal] = s.scan + BAN_SCANS;
      this.setGoal(-1, false);
    }
  }

  private setGoal(goal: number, exploring: boolean): void {
    if (goal !== this.goal) {
      this.goal = goal;
      this.goalBest = INF;
      this.stall = 0;
    }
    this.exploring = exploring;
  }

  private chooseGoal(s: CaveState, me: number, short: boolean): number {
    this.fuse = short ? this.hunter.plan(s, me, this.near, this.banned, this.exploring ? this.goal : -1) : null;
    if (this.fuse !== null) {
      this.setGoal(this.fuse.at, true);
      return this.fuse.at;
    }
    const target = pickTarget(s, this.near, this.banned, this.exploring ? -1 : this.goal);
    if (target >= 0) {
      this.setGoal(target, false);
      return target;
    }
    if (this.exploring && this.goal >= 0) {
      if (this.goal === me) this.banned[me] = s.scan + VISITED_SCANS;
      else if (this.near.reachable(this.goal) && (this.banned[this.goal] ?? 0) <= s.scan) return this.goal;
    }
    const g = this.explorer.goal(s, me, this.near, this.banned, this.rng);
    this.setGoal(g, true);
    return g;
  }
}

/**
 * A new cave (other params), or the same one restarted (the scan count went back), gets a fresh
 * mind; the running state is expected to be the engine's own, stepped in place.
 */
class Pilot implements Autopilot {
  private readonly rng: Rng;
  private mind: Mind | null = null;
  private params: CaveState['params'] | null = null;
  private lastScan = -1;

  constructor(seed: number) {
    this.rng = new Rng(seed);
  }

  next(s: CaveState): Input {
    if (!s.digby.born || !s.digby.alive || s.status !== 'playing') return STAY;
    if (this.mind === null || this.params !== s.params || s.scan < this.lastScan) {
      this.mind = new Mind(s, this.rng);
      this.params = s.params;
    }
    this.lastScan = s.scan;
    return this.mind.decide(s);
  }
}

/** A deterministic autopilot; `seed` drives its tie-breaking when it has to explore. */
export function createAutopilot(options: { seed?: number } = {}): Autopilot {
  return new Pilot(options.seed ?? 1);
}
